import { Carousel as CarouselComponent } from '@/ui';
import { WidthOptions } from '@types';
import { clsx } from 'clsx';

import { FlexibleData, PageRenderTransition } from '@/lib/server';

import { PageRender } from '../util/page-render';
import { BlockBase, Wrapper, childClassNames, columnsClassName, getMarginClassNames } from './utils';

interface UICarousel extends BlockBase {
  slides: [{ content: FlexibleData }];
  cardsPerSlide: string;
  blockWidth?: WidthOptions;
  showNavigationArrows?: boolean;
  autoPlay?: boolean;
  /** @deprecated */
  showProgressIndicator?: boolean;
  transition?: PageRenderTransition;
}

export const basisOptions = {
  1: 'sm:basis-1/1',
  2: 'sm:basis-1/2',
  3: 'sm:basis-1/2 lg:basis-1/3',
  4: 'sm:basis-1/2 lg:basis-1/4',
  5: 'sm:basis-1/2 lg:basis-1/4 xl:basis-1/5',
  6: 'sm:basis-1/2 lg:basis-1/4 xl:basis-1/6',
  7: 'sm:basis-1/2 lg:basis-1/4 xl:basis-1/6 2xl:basis-1/7',
  8: 'sm:basis-1/2 lg:basis-1/4 xl:basis-1/6 2xl:basis-1/8',
};

export function Carousel({
  params,
  slides,
  showNavigationArrows,
  cardsPerSlide,
  transition,
  autoPlay,
  blockWidth,
  id,
  margin,

  // destructure so it's not passed to the DOM
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showProgressIndicator,

  ...rest
}: UICarousel) {
  if (!slides?.length) {
    return null;
  }

  if (slides?.length === 1) {
    const slide = slides?.[0];
    return (
      <Wrapper id={id} className={getMarginClassNames(margin)}>
        <PageRender params={params} data={slide.content} />
      </Wrapper>
    );
  }

  return (
    <CarouselComponent
      id={id}
      autoPlayDuration={autoPlay ? 4000 : undefined}
      hideButtons={!showNavigationArrows}
      layout="padded"
      className={clsx(getMarginClassNames(margin), {
        'max-w-none': blockWidth === 'full',
        'max-w-[theme(screens.3xl)]': blockWidth === 'desktop',
        'max-w-(--breakpoint-2xl)': blockWidth === 'wide' || !blockWidth,
        'max-w-(--breakpoint-xl)': blockWidth === 'standard',
        'max-w-(--breakpoint-lg)': blockWidth === 'narrow',
        'max-w-(--breakpoint-md)': blockWidth === 'prose',
      })}
      classNames={{
        carouselItem: clsx(basisOptions[Number(cardsPerSlide) as keyof typeof basisOptions], columnsClassName),
      }}
      {...rest}
      items={slides.map((slide, idx) => (
        <PageRender
          key={`${id}-${idx}`}
          transition={
            transition && {
              name: transition.name,
              delay: idx * 0.2,
              classNames: childClassNames,
            }
          }
          params={params}
          data={slide.content}
        />
      ))}
    />
  );
}
